// Records the viewer camera as a benchmark camera path (dev-scripts/benchmark, `--path-file`).
// Poses are sampled at a fixed interval; a pose that did not change extends the pause of the
// previous key instead of adding a key, so a recording of a short look-around stays small.

export type HsCameraPathVector = [number, number, number];

export interface HsCameraPathKey {
  position: HsCameraPathVector;
  direction: HsCameraPathVector;
  /** Travel time from the previous key, in seconds. */
  seconds?: number;
  /** Time the camera stays at this key before moving on, in seconds. */
  pause?: number;
}

export interface HsRecordedCameraPath {
  name: string;
  sceneId: string | null;
  recordedAt: string;
  keys: HsCameraPathKey[];
}

const POSITION_EPSILON: number = 1e-3;
const DIRECTION_EPSILON: number = 1e-4;

function round(value: number, digits: number): number {
  const scale: number = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function differs(a: HsCameraPathVector, b: HsCameraPathVector, epsilon: number): boolean {
  return a.some((value: number, index: number) => Math.abs(value - b[index]) > epsilon);
}

export class HsCameraPathRecorder {
  private keys: HsCameraPathKey[] = [];
  private lastSampleAt: number = 0;
  private lastMoveAt: number = 0;

  public get keyCount(): number {
    return this.keys.length;
  }

  public start(position: HsCameraPathVector, direction: HsCameraPathVector, now: number): void {
    this.keys = [{ position: this.roundVector(position, 3), direction: this.roundVector(direction, 5) }];
    this.lastSampleAt = now;
    this.lastMoveAt = now;
  }

  public sample(position: HsCameraPathVector, direction: HsCameraPathVector, now: number): void {
    const previous: HsCameraPathKey | undefined = this.keys[this.keys.length - 1];
    if (previous === undefined) {
      this.start(position, direction, now);
      return;
    }
    const moved: boolean =
      differs(position, previous.position, POSITION_EPSILON) ||
      differs(direction, previous.direction, DIRECTION_EPSILON);
    if (moved) {
      const pauseSeconds: number = (this.lastSampleAt - this.lastMoveAt) / 1000;
      if (pauseSeconds > 0) {
        previous.pause = round(pauseSeconds, 2);
      }
      this.keys.push({
        position: this.roundVector(position, 3),
        direction: this.roundVector(direction, 5),
        seconds: round((now - this.lastSampleAt) / 1000, 3),
      });
      this.lastMoveAt = now;
    }
    this.lastSampleAt = now;
  }

  public finish(name: string, sceneId: string | null, recordedAt: Date): HsRecordedCameraPath {
    return {
      name,
      sceneId,
      recordedAt: recordedAt.toISOString(),
      keys: this.keys.map((key: HsCameraPathKey) => ({ ...key })),
    };
  }

  private roundVector(vector: HsCameraPathVector, digits: number): HsCameraPathVector {
    return [round(vector[0], digits), round(vector[1], digits), round(vector[2], digits)];
  }
}
